import React, { useEffect, useMemo, useState } from 'react';
import { FileSpreadsheet, RefreshCw, Save, Trophy } from 'lucide-react';
import toast from 'react-hot-toast';
import { api, formatDate } from '../services/api';

interface ConfigPremiacao {
  valor_por_perna: number;
  atualizado_em: string | null;
  atualizado_por_nome: string | null;
}

interface ResumoMotorista {
  motorista_id: number | null;
  motorista_nome: string;
  total_pernas: number;
  pernas_qr: number;
  pernas_manuais: number;
  por_trecho: Record<string, number>;
  valor_total: number;
}

type ViaInicio = 'qrcode' | 'manual';

interface Perna {
  fase_id: number;
  os_id: number;
  numero_nota_contratacao: string | number | null;
  codigo_acesso: string;
  nome_falecido: string | null;
  os_criado_em: string;
  motorista_id: number | null;
  motorista_nome: string;
  veiculo_placa: string | null;
  polo: string | null;
  fase: string;
  trecho: string;
  inicio: string | null;
  chegada: string | null;
  via: ViaInicio;
  valor: number;
}

interface RelatorioPremiacao {
  periodo: { data_inicio: string; data_fim: string };
  valor_por_perna: number;
  trechos: string[];
  total_pernas: number;
  pernas_qr: number;
  pernas_manuais: number;
  valor_total: number;
  resumo: ResumoMotorista[];
  pernas: Perna[];
}

type TipoPeriodo = 'mensal' | 'trimestral' | 'anual' | 'personalizado';

const hoje = new Date();
const ANO_ATUAL = hoje.getFullYear();
const ANOS_DISPONIVEIS = Array.from({ length: 6 }, (_, i) => ANO_ATUAL - i);

const toISODate = (ano: number, mes: number, dia: number): string =>
  `${ano}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;

const ultimoDiaDoMes = (ano: number, mes: number): number => new Date(ano, mes, 0).getDate();

const formatarDataBR = (iso: string): string => iso.split('-').reverse().join('-');

const formatarMoeda = (valor: number): string =>
  valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const extrairErroBlob = async (error: any): Promise<string | undefined> => {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text())?.error;
    } catch {
      return undefined;
    }
  }
  return data?.error;
};

const chaveMotorista = (id: number | null): string => String(id ?? 0);

const Premiacao: React.FC = () => {
  const [config, setConfig] = useState<ConfigPremiacao | null>(null);
  const [valorInput, setValorInput] = useState('');
  const [salvando, setSalvando] = useState(false);

  const [periodo, setPeriodo] = useState({
    tipo: 'mensal' as TipoPeriodo,
    ano: ANO_ATUAL,
    mes: hoje.getMonth() + 1,
    trimestre: Math.floor(hoje.getMonth() / 3) + 1,
    data_inicio: '',
    data_fim: ''
  });
  const [relatorio, setRelatorio] = useState<RelatorioPremiacao | null>(null);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [motoristaFiltro, setMotoristaFiltro] = useState('');
  const [viaFiltro, setViaFiltro] = useState<'' | ViaInicio>('');

  const calcularPeriodo = (): { data_inicio: string; data_fim: string } | null => {
    const { tipo, ano, mes, trimestre, data_inicio, data_fim } = periodo;
    if (tipo === 'mensal') {
      return { data_inicio: toISODate(ano, mes, 1), data_fim: toISODate(ano, mes, ultimoDiaDoMes(ano, mes)) };
    }
    if (tipo === 'trimestral') {
      const mesInicial = (trimestre - 1) * 3 + 1;
      const mesFinal = mesInicial + 2;
      return {
        data_inicio: toISODate(ano, mesInicial, 1),
        data_fim: toISODate(ano, mesFinal, ultimoDiaDoMes(ano, mesFinal))
      };
    }
    if (tipo === 'anual') {
      return { data_inicio: toISODate(ano, 1, 1), data_fim: toISODate(ano, 12, 31) };
    }
    if (!data_inicio || !data_fim) return null;
    return { data_inicio, data_fim };
  };

  const validarPeriodo = (): { data_inicio: string; data_fim: string } | null => {
    const p = calcularPeriodo();
    if (!p) {
      toast.error('Informe as datas inicial e final do período.');
      return null;
    }
    if (p.data_fim < p.data_inicio) {
      toast.error('A data final deve ser igual ou posterior à inicial.');
      return null;
    }
    return p;
  };

  const carregarConfig = async () => {
    try {
      const response = await api.get('/premiacao/config');
      const cfg: ConfigPremiacao = response.data.config;
      setConfig(cfg);
      setValorInput(cfg.valor_por_perna.toFixed(2).replace('.', ','));
    } catch (error: any) {
      console.error('Erro ao carregar configuração da premiação:', error);
      toast.error(error.response?.data?.error || 'Erro ao carregar o valor por perna');
    }
  };

  const salvarValor = async () => {
    const valor = Number(valorInput.replace(/\./g, '').replace(',', '.'));
    if (!valorInput.trim() || Number.isNaN(valor) || valor < 0) {
      toast.error('Informe um valor por perna válido.');
      return;
    }

    try {
      setSalvando(true);
      const response = await api.put('/premiacao/config', { valor_por_perna: Math.round(valor * 100) / 100 });
      const cfg: ConfigPremiacao = response.data.config;
      setConfig(cfg);
      setValorInput(cfg.valor_por_perna.toFixed(2).replace('.', ','));
      toast.success('Valor por perna salvo');
      if (relatorio) carregarRelatorio();
    } catch (error: any) {
      console.error('Erro ao salvar valor por perna:', error);
      toast.error(error.response?.data?.error || 'Erro ao salvar o valor por perna');
    } finally {
      setSalvando(false);
    }
  };

  const carregarRelatorio = async () => {
    const p = validarPeriodo();
    if (!p) return;

    try {
      setLoading(true);
      const response = await api.get('/premiacao', { params: p, timeout: 60000 });
      setRelatorio(response.data);
      setMotoristaFiltro('');
      setViaFiltro('');
    } catch (error: any) {
      console.error('Erro ao carregar premiação:', error);
      toast.error(error.response?.data?.error || 'Erro ao carregar a premiação');
      setRelatorio(null);
    } finally {
      setLoading(false);
    }
  };

  const exportarExcel = async () => {
    const p = validarPeriodo();
    if (!p) return;

    try {
      setExportando(true);
      const response = await api.get('/premiacao/exportar', {
        params: p,
        responseType: 'blob',
        timeout: 180000
      });

      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Premiacao Motoristas - ${formatarDataBR(p.data_inicio)} a ${formatarDataBR(p.data_fim)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success('Planilha de premiação gerada com sucesso');
    } catch (error: any) {
      console.error('Erro ao exportar premiação:', error);
      toast.error((await extrairErroBlob(error)) || 'Erro ao gerar a planilha de premiação');
    } finally {
      setExportando(false);
    }
  };

  useEffect(() => {
    carregarConfig();
    carregarRelatorio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pernasFiltradas = useMemo(() => {
    if (!relatorio) return [];
    return relatorio.pernas.filter(
      (p) =>
        (!motoristaFiltro || chaveMotorista(p.motorista_id) === motoristaFiltro) &&
        (!viaFiltro || p.via === viaFiltro)
    );
  }, [relatorio, motoristaFiltro, viaFiltro]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <Trophy className="h-7 w-7 text-amber-500" />
            Premiação dos Motoristas
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Todas as pernas concluídas com o corpo no veículo. Só as iniciadas pelo motorista com leitura do QR Code são
            premiadas; as feitas manualmente aparecem com valor zero.
          </p>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="text-sm font-medium text-gray-700 mb-3">Valor por perna</div>
        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Valor (R$)</label>
            <input
              type="text"
              inputMode="decimal"
              value={valorInput}
              onChange={(e) => setValorInput(e.target.value.replace(/[^\d.,]/g, ''))}
              onKeyDown={(e) => e.key === 'Enter' && salvarValor()}
              placeholder="0,00"
              className="w-full sm:w-40 rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <button
            type="button"
            onClick={salvarValor}
            disabled={salvando || !config}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Save className="h-4 w-4" />
            {salvando ? 'Salvando…' : 'Salvar'}
          </button>
          {config?.atualizado_em && (
            <span className="text-xs text-gray-400 sm:pb-2">
              Atualizado em {formatDate(config.atualizado_em)}
              {config.atualizado_por_nome ? ` por ${config.atualizado_por_nome}` : ''}
            </span>
          )}
        </div>
        <p className="mt-3 text-xs text-gray-400">
          O cálculo usa sempre o valor atual: ao alterá-lo, os relatórios de períodos anteriores também mudam.
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="text-sm font-medium text-gray-700 mb-3">Período (data de criação da OS)</div>
        <div className="flex flex-col lg:flex-row lg:items-end gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Período</label>
            <select
              value={periodo.tipo}
              onChange={(e) => setPeriodo((prev) => ({ ...prev, tipo: e.target.value as TipoPeriodo }))}
              className="w-full lg:w-44 rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="mensal">Mensal</option>
              <option value="trimestral">Trimestral</option>
              <option value="anual">Anual</option>
              <option value="personalizado">Personalizado</option>
            </select>
          </div>

          {periodo.tipo === 'mensal' && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Mês</label>
              <select
                value={periodo.mes}
                onChange={(e) => setPeriodo((prev) => ({ ...prev, mes: Number(e.target.value) }))}
                className="w-full lg:w-40 rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {new Date(2000, i, 1).toLocaleDateString('pt-BR', { month: 'long' })}
                  </option>
                ))}
              </select>
            </div>
          )}

          {periodo.tipo === 'trimestral' && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Trimestre</label>
              <select
                value={periodo.trimestre}
                onChange={(e) => setPeriodo((prev) => ({ ...prev, trimestre: Number(e.target.value) }))}
                className="w-full lg:w-44 rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                <option value={1}>1º (jan–mar)</option>
                <option value={2}>2º (abr–jun)</option>
                <option value={3}>3º (jul–set)</option>
                <option value={4}>4º (out–dez)</option>
              </select>
            </div>
          )}

          {periodo.tipo !== 'personalizado' && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Ano</label>
              <select
                value={periodo.ano}
                onChange={(e) => setPeriodo((prev) => ({ ...prev, ano: Number(e.target.value) }))}
                className="w-full lg:w-28 rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                {ANOS_DISPONIVEIS.map((ano) => (
                  <option key={ano} value={ano}>{ano}</option>
                ))}
              </select>
            </div>
          )}

          {periodo.tipo === 'personalizado' && (
            <>
              <div>
                <label className="block text-xs text-gray-500 mb-1">De</label>
                <input
                  type="date"
                  value={periodo.data_inicio}
                  onChange={(e) => setPeriodo((prev) => ({ ...prev, data_inicio: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Até</label>
                <input
                  type="date"
                  value={periodo.data_fim}
                  onChange={(e) => setPeriodo((prev) => ({ ...prev, data_fim: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
            </>
          )}

          <button
            type="button"
            onClick={carregarRelatorio}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Consultar
          </button>
          <button
            type="button"
            onClick={exportarExcel}
            disabled={exportando}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <FileSpreadsheet className={`h-4 w-4 ${exportando ? 'animate-pulse' : ''}`} />
            {exportando ? 'Gerando…' : 'Exportar Excel'}
          </button>
        </div>
        <p className="mt-3 text-xs text-gray-400">
          Não são pernas: a fase Remoção (polo até o local de remoção, sem corpo) e o sepultamento no mesmo local do
          velório. Pernas sem leitura do QR Code (iniciadas pelo Admin ou antes da obrigatoriedade do QR) saem como
          manuais, com valor zero. Período máximo de 366 dias.
        </p>
      </div>

      {relatorio && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-xs text-gray-500">Total de pernas</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900">{relatorio.total_pernas}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-xs text-gray-500">Via QR Code (premiadas)</div>
            <div className="mt-1 text-2xl font-semibold text-green-700">{relatorio.pernas_qr}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-xs text-gray-500">Manuais (sem valor)</div>
            <div className="mt-1 text-2xl font-semibold text-amber-600">{relatorio.pernas_manuais}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-xs text-gray-500">Valor por perna</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900">{formatarMoeda(relatorio.valor_por_perna)}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-xs text-gray-500">Valor total</div>
            <div className="mt-1 text-2xl font-semibold text-green-700">{formatarMoeda(relatorio.valor_total)}</div>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 text-sm font-medium text-gray-700">Resumo por motorista</div>
        {loading ? (
          <div className="p-8 text-center text-gray-500">Carregando…</div>
        ) : !relatorio || relatorio.resumo.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Nenhuma perna encontrada no período.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 text-gray-600 text-xs uppercase">
                <tr>
                  <th className="px-3 py-2 text-left">Motorista</th>
                  <th className="px-3 py-2 text-right">Pernas</th>
                  <th className="px-3 py-2 text-right">Via QR</th>
                  <th className="px-3 py-2 text-right">Manuais</th>
                  {relatorio.trechos.map((t) => (
                    <th key={t} className="px-3 py-2 text-right normal-case">{t}</th>
                  ))}
                  <th className="px-3 py-2 text-right">Valor total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {relatorio.resumo.map((r) => {
                  const chave = chaveMotorista(r.motorista_id);
                  const selecionado = motoristaFiltro === chave;
                  return (
                    <tr
                      key={chave}
                      onClick={() => setMotoristaFiltro(selecionado ? '' : chave)}
                      className={`cursor-pointer hover:bg-gray-50 ${selecionado ? 'bg-blue-50' : ''}`}
                    >
                      <td className="px-3 py-2 text-gray-900 font-medium">{r.motorista_nome}</td>
                      <td className="px-3 py-2 text-right text-gray-900">{r.total_pernas}</td>
                      <td className="px-3 py-2 text-right text-green-700">{r.pernas_qr}</td>
                      <td className={`px-3 py-2 text-right ${r.pernas_manuais ? 'text-amber-600 font-medium' : 'text-gray-400'}`}>
                        {r.pernas_manuais}
                      </td>
                      {relatorio.trechos.map((t) => (
                        <td key={t} className="px-3 py-2 text-right text-gray-600">{r.por_trecho[t] || 0}</td>
                      ))}
                      <td className="px-3 py-2 text-right font-medium text-green-700">{formatarMoeda(r.valor_total)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {relatorio && relatorio.pernas.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <span className="text-sm font-medium text-gray-700">
              Pernas ({pernasFiltradas.length})
            </span>
            <div className="flex flex-col sm:flex-row gap-2">
              <select
                value={viaFiltro}
                onChange={(e) => setViaFiltro(e.target.value as '' | ViaInicio)}
                className="w-full sm:w-48 rounded-md border border-gray-300 px-3 py-1.5 text-sm"
              >
                <option value="">QR Code e manuais</option>
                <option value="qrcode">Só via QR Code</option>
                <option value="manual">Só manuais</option>
              </select>
              <select
                value={motoristaFiltro}
                onChange={(e) => setMotoristaFiltro(e.target.value)}
                className="w-full sm:w-64 rounded-md border border-gray-300 px-3 py-1.5 text-sm"
              >
                <option value="">Todos os motoristas</option>
                {relatorio.resumo.map((r) => (
                  <option key={chaveMotorista(r.motorista_id)} value={chaveMotorista(r.motorista_id)}>
                    {r.motorista_nome}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 text-gray-600 text-xs uppercase">
                <tr>
                  <th className="px-3 py-2 text-left">Motorista</th>
                  <th className="px-3 py-2 text-left">OS</th>
                  <th className="px-3 py-2 text-left">Data da OS</th>
                  <th className="px-3 py-2 text-left">Trecho</th>
                  <th className="px-3 py-2 text-left">Início</th>
                  <th className="px-3 py-2 text-left">Chegada</th>
                  <th className="px-3 py-2 text-left">Veículo</th>
                  <th className="px-3 py-2 text-left">Polo</th>
                  <th className="px-3 py-2 text-left">Iniciada via</th>
                  <th className="px-3 py-2 text-right">Valor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pernasFiltradas.map((p) => (
                  <tr key={p.fase_id} className={p.via === 'manual' ? 'bg-amber-50' : ''}>
                    <td className="px-3 py-2 text-gray-900">{p.motorista_nome}</td>
                    <td className="px-3 py-2">
                      <div className="text-gray-900">{p.nome_falecido || '—'}</div>
                      <div className="text-xs text-gray-500">
                        Nota {p.numero_nota_contratacao || '—'} · {p.codigo_acesso}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                      {new Date(p.os_criado_em).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                    </td>
                    <td className="px-3 py-2 text-gray-900">{p.trecho}</td>
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{formatDate(p.inicio || '')}</td>
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{formatDate(p.chegada || '')}</td>
                    <td className="px-3 py-2 text-gray-600">{p.veiculo_placa || '—'}</td>
                    <td className="px-3 py-2 text-gray-600">{p.polo || '—'}</td>
                    <td className="px-3 py-2">
                      {p.via === 'qrcode' ? (
                        <span className="inline-flex px-2 py-0.5 text-xs rounded border bg-green-100 text-green-800 border-green-200">
                          QR Code
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-0.5 text-xs rounded border bg-amber-100 text-amber-800 border-amber-200">
                          Manual
                        </span>
                      )}
                    </td>
                    <td className={`px-3 py-2 text-right whitespace-nowrap ${p.via === 'qrcode' ? 'text-gray-900' : 'text-gray-400'}`}>
                      {formatarMoeda(p.valor)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Premiacao;
